import React, { useState, useEffect, useCallback } from "react";
import { COLORS } from "../../../../theme/colors";
import { MATERIALS } from "../../../../theme/materials";
import { agentApi, workflowApi, scheduleApi } from "../../../../api/client";
import TabBtn from "../../../../components/TabBtn";

// agent-experience-redesign-v1.md §6.3 (C6) — dual-mode bind modal
// for workflows. Two tabs:
//   tab (a) "Existing"  — pick from the tenant's workflows table
//   tab (b) "Create new" — minimal cron-triggered job form;
//                          falls back to scheduleApi.create which is
//                          the SchedulesView path (cron + agent +
//                          output routing).
//
// Workflow binding mutates workflows.entry_agent_id. Schedules live
// in a separate scheduled_jobs table — we use the schedule path for
// the "create new + auto-attach to this agent" use case since that's
// what the design's Q9 = "both" resolution captured (mirrors the
// existing SchedulesView create-job flow).

// Pure helper (named export for unit testing) — unwraps workflowApi.list()'s
// response. Preferred shape is { data: { workflows: [...] } }; falls back to a
// bare { data: [...] } array for the older shape. Extracted from the fetch
// effect below and wired back there.
export function unwrapWorkflows(res) {
  return res?.data?.workflows || (Array.isArray(res?.data) ? res.data : []);
}

export default function BindWorkflowModal({ agentId, agentName, onClose, onBound }) {
  const [tab, setTab] = useState("existing");
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  // Create-new form state — minimal cron job per design §6.3.
  const [form, setForm] = useState({
    name: "",
    description: "",
    cron_expression: "0 9 * * *", // 9am daily default
    timezone: "UTC",
    message: "",
    output_action: "log",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    workflowApi.list()
      // workflowApi.list() returns { success, data: { workflows: [...] } } per
      // BuilderView pattern; defensive fallback to data array if older shape.
      .then(res => {
        if (!cancelled) {
          setList(unwrapWorkflows(res));
        }
      })
      .catch(e => { if (!cancelled) setError(e?.message || String(e)); })
      .finally(() => { if (!cancelled) setLoadingList(false); });
    return () => { cancelled = true; };
  }, []);

  const bindExisting = useCallback(async (workflowId) => {
    setBusyId(workflowId);
    setError(null);
    try {
      await agentApi.bindWorkflow(agentId, workflowId);
      onBound && onBound(workflowId);
      onClose && onClose();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusyId(null);
    }
  }, [agentId, onBound, onClose]);

  const submitCreate = useCallback(async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!form.name.trim()) return setError("Name is required");
    if (!form.cron_expression.trim()) return setError("Cron expression is required");
    setSubmitting(true);
    try {
      // Schedules-side: create a scheduled job pre-attached to this
      // agent. This mirrors SchedulesView's create-job flow rather
      // than the workflows table (which is a heavier surface).
      // Per design §6.3 Q9: "tab (b) Create new runs the SchedulesView
      // create-job modal pre-filled to this agent."
      const created = await scheduleApi.create({
        name: form.name,
        description: form.description || null,
        cron_expression: form.cron_expression,
        timezone: form.timezone || "UTC",
        agent_id: agentId,
        message: form.message || null,
        output_action: form.output_action,
      });
      const newId = created?.data?.id || created?.id;
      onBound && onBound(newId);
      onClose && onClose();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }, [form, agentId, onBound, onClose]);

  // Show only workflows not already bound to this agent.
  const visibleList = list.filter(w => w.entry_agent_id !== agentId);

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: 12, width: "90%", maxWidth: 540, maxHeight: "85vh",
        overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: MATERIALS?.spatialShadow ? MATERIALS.spatialShadow() : "0 16px 40px rgba(0,0,0,0.5)",
      }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>
            Bind workflow {agentName ? <span style={{ color: COLORS.textMuted, fontWeight: 500 }}>· {agentName}</span> : null}
          </div>
          <button type="button" onClick={onClose} style={{
            background: "transparent", border: "none", color: COLORS.textDim,
            cursor: "pointer", fontSize: 16,
          }}>✕</button>
        </div>

        <div style={{ display: "flex", borderBottom: `1px solid ${COLORS.border}`, background: COLORS.surface }}>
          <TabBtn active={tab === "existing"} onClick={() => setTab("existing")} label="Existing" />
          <TabBtn active={tab === "create"} onClick={() => setTab("create")} label="Create new (cron)" />
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          {error && (
            <div style={{
              padding: "8px 12px", borderRadius: 6, marginBottom: 12,
              background: `${COLORS.pink || "#ef4444"}15`,
              border: `1px solid ${COLORS.pink || "#ef4444"}55`,
              color: COLORS.pink || "#ef4444", fontSize: 12,
            }}>{error}</div>
          )}

          {tab === "existing" && (
            <>
              {loadingList ? (
                <div style={{ color: COLORS.textDim, fontSize: 12 }}>Loading workflows…</div>
              ) : visibleList.length === 0 ? (
                <div style={{ color: COLORS.textDim, fontSize: 12, lineHeight: 1.5 }}>
                  No unbound workflows in your tenant. Switch to <strong>Create new (cron)</strong> to schedule a new job.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {visibleList.map(w => (
                    <div key={w.id} style={{
                      padding: 12, borderRadius: 8,
                      border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                      display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{w.name}</div>
                        <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>
                          v{w.version_number || 1} · {w.status || "draft"}
                        </div>
                        {w.description && (
                          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>{w.description}</div>
                        )}
                      </div>
                      <button type="button"
                        disabled={busyId === w.id}
                        onClick={() => bindExisting(w.id)}
                        style={{
                          padding: "5px 14px", borderRadius: 6, border: "none",
                          background: COLORS.accent, color: "#fff",
                          fontSize: 11, fontWeight: 600, cursor: busyId === w.id ? "default" : "pointer",
                          opacity: busyId === w.id ? 0.6 : 1, whiteSpace: "nowrap",
                        }}>
                        {busyId === w.id ? "Binding…" : "Bind"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === "create" && (
            <form onSubmit={submitCreate} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Field label="Name *" value={form.name}
                     onChange={(v) => setForm(p => ({ ...p, name: v }))}
                     placeholder="Daily morning summary" />
              <Field label="Description" value={form.description}
                     onChange={(v) => setForm(p => ({ ...p, description: v }))}
                     placeholder="What does this scheduled job do?"
                     textarea />
              <Field label="Cron expression *" value={form.cron_expression}
                     onChange={(v) => setForm(p => ({ ...p, cron_expression: v }))}
                     placeholder="0 9 * * *"
                     hint="Minute Hour DayOfMonth Month DayOfWeek (UTC by default)" />
              <Field label="Timezone" value={form.timezone}
                     onChange={(v) => setForm(p => ({ ...p, timezone: v }))}
                     placeholder="UTC | America/New_York | …" />
              <Field label="Message" value={form.message}
                     onChange={(v) => setForm(p => ({ ...p, message: v }))}
                     placeholder="Prompt to send the agent at fire time"
                     textarea />
              <div>
                <label style={labelStyle}>Output action</label>
                <select
                  value={form.output_action}
                  onChange={(e) => setForm(p => ({ ...p, output_action: e.target.value }))}
                  style={fieldStyle}
                >
                  <option value="log">Log only</option>
                  <option value="email">Email</option>
                  <option value="webhook">Webhook</option>
                </select>
              </div>
              <div style={{ fontSize: 10, color: COLORS.textDim, lineHeight: 1.5, marginTop: 4 }}>
                Email + webhook routing is configured in Schedules → job detail after creation.
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
                <button type="button" onClick={onClose} style={{
                  background: "transparent", border: `1px solid ${COLORS.border}`,
                  color: COLORS.textDim, padding: "6px 14px", borderRadius: 6,
                  fontSize: 12, cursor: "pointer",
                }}>Cancel</button>
                <button type="submit" disabled={submitting} style={{
                  background: COLORS.accent, color: "#fff", border: "none",
                  padding: "6px 14px", borderRadius: 6,
                  fontSize: 12, fontWeight: 600, cursor: submitting ? "default" : "pointer",
                  opacity: submitting ? 0.6 : 1,
                }}>
                  {submitting ? "Creating…" : "Create + Bind"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, hint, textarea }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      {textarea ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          style={fieldStyle}
        />
      ) : (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={fieldStyle}
        />
      )}
      {hint && (
        <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 3 }}>{hint}</div>
      )}
    </div>
  );
}

const labelStyle = {
  display: "block", fontSize: 11, fontWeight: 600,
  color: COLORS.textMuted, marginBottom: 4, letterSpacing: "0.02em",
};

const fieldStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "8px 12px", fontSize: 12, color: COLORS.text,
  background: COLORS.surface,
  border: `1px solid ${COLORS.border}`, borderRadius: 6,
  outline: "none", fontFamily: "inherit",
};
